import {
  AppBar, Box, Divider, Drawer, List, ListItemButton, ListItemText, Toolbar, Typography, Button, Chip, useMediaQuery,
} from '@mui/material';
import { useState } from 'react';
import type { JSX } from 'react';
import { Outlet, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../auth/AuthProvider.js';

const NAV_ITEMS: Array<{ path: string; label: string; adminOnly?: boolean }> = [
  { path: '/overview', label: 'Visão geral' },
  { path: '/users', label: 'Contas' },
  { path: '/circles', label: 'Círculos' },
  { path: '/consents', label: 'Consentimentos' },
  { path: '/plans', label: 'Planos' },
  { path: '/billing', label: 'Cobrança', adminOnly: true },
  { path: '/jobs', label: 'Jobs' },
  { path: '/audit', label: 'Auditoria' },
  { path: '/ai', label: 'IA' },
  { path: '/content', label: 'Conteúdo' },
  { path: '/notifications', label: 'Notificações' },
  { path: '/privacy', label: 'Privacidade' },
  { path: '/security', label: 'Segurança' },
  { path: '/settings', label: 'Configurações' },
];

const DRAWER_WIDTH = 248;

export function AppShell(): JSX.Element {
  const { session, signOut } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const compact = useMediaQuery('(max-width: 839px)');
  const [mobileOpen, setMobileOpen] = useState(false);

  const isAdmin = session?.platformRoles.includes('platform_admin') ?? false;
  const items = NAV_ITEMS.filter((item) => !item.adminOnly || isAdmin);

  const drawer = (
    <Box component="nav" aria-label="Navegação principal" sx={{ width: DRAWER_WIDTH }}>
      <Toolbar sx={{ gap: 1 }}>
        <Typography variant="h2" component="span" sx={{ color: 'primary.main' }}>
          CareMesh
        </Typography>
        <Chip size="small" label="operação" />
      </Toolbar>
      <Divider />
      <List>
        {items.map((item) => (
          <ListItemButton
            key={item.path}
            selected={location.pathname.startsWith(item.path)}
            onClick={() => {
              navigate(item.path);
              setMobileOpen(false);
            }}
          >
            <ListItemText primary={item.label} />
          </ListItemButton>
        ))}
      </List>
    </Box>
  );

  return (
    <Box sx={{ display: 'flex', minHeight: '100vh' }}>
      {compact ? (
        <Drawer open={mobileOpen} onClose={() => setMobileOpen(false)}>
          {drawer}
        </Drawer>
      ) : (
        <Drawer variant="permanent" open>
          {drawer}
        </Drawer>
      )}
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <AppBar position="sticky" color="inherit" elevation={1}>
          <Toolbar sx={{ gap: 2 }}>
            {compact ? (
              <Button onClick={() => setMobileOpen(true)} aria-label="Abrir navegação">
                Menu
              </Button>
            ) : null}
            <Typography sx={{ flex: 1 }} variant="body2" color="text.secondary">
              Painel operacional — ações administrativas são auditadas.
            </Typography>
            <Typography variant="body2">{session?.user.name}</Typography>
            <Button onClick={() => void signOut()}>Sair</Button>
          </Toolbar>
        </AppBar>
        <Box component="main" sx={{ p: { xs: 2, md: 3 }, maxWidth: 1200 }}>
          <Outlet />
        </Box>
      </Box>
    </Box>
  );
}